"use client";

import { datadogLogs } from "@datadog/browser-logs";
import { datadogRum } from "@datadog/browser-rum";
import { useEffect } from "react";
import { useOptionalAuthenticatedProfile } from "src/app/AuthenticatedProfileProvider";
import { setDatadogUser } from "src/lib/monitoring/datadogUser";

function getEnv() {
  const currentBranch = process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_REF;

  if (process.env.NEXT_PUBLIC_VERCEL_ENV === "production") {
    return "prod";
  }
  if (currentBranch === "main") {
    return "dev";
  }
  return currentBranch;
}

function isDatadogEnabled() {
  return (
    process.env.NEXT_PUBLIC_VERCEL_ENV === "production" ||
    process.env.NEXT_PUBLIC_VERCEL_ENV === "preview"
  );
}

function isAutomatedTestBrowser() {
  return typeof navigator !== "undefined" && navigator.webdriver === true;
}

// Global flag to prevent multiple initializations across component remounts
// This is necessary because React Strict Mode will mount/unmount components twice in dev
let globalInitAttempted = false;

// Check if Datadog is already initialized (e.g., by the legacy app on the same domain)
function isDatadogAlreadyInitialized() {
  if (typeof window === "undefined") return false;

  try {
    // Check if datadogRum has already been initialized (e.g., by web-legacy on the same subdomain)
    return datadogRum.getInternalContext() !== undefined;
  } catch {
    return false;
  }
}

// Initialize Datadog in a controlled manner
function initializeDatadog() {
  // Skip if not in browser environment
  if (typeof window === "undefined") {
    return false;
  }

  if (isAutomatedTestBrowser()) {
    return false;
  }

  // Skip if we've already attempted initialization globally (prevents React Strict Mode double-init)
  if (globalInitAttempted) {
    return isDatadogAlreadyInitialized();
  }

  // If already initialized (e.g., by legacy app on same domain), still ensure
  // session replay recording is active for web-next views in this session.
  if (isDatadogAlreadyInitialized()) {
    globalInitAttempted = true;
    try {
      // Safe to call repeatedly: the SDK ignores this when recording is already active.
      datadogRum.startSessionReplayRecording();
    } catch {
      // Avoid breaking the app if replay cannot be started from a legacy-owned RUM session.
    }
    return true;
  }

  // Mark that we're attempting initialization
  globalInitAttempted = true;

  try {
    // Datadog RUM configuration definition: https://docs.datadoghq.com/real_user_monitoring/browser/
    datadogLogs.init({
      clientToken: "pub991a3cacb1eba9538678833aec0ac23b",
      site: "datadoghq.com",
      service: "web-next",
      env: getEnv(),
      version: process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA,
      forwardConsoleLogs: ["error", "warn"],
      forwardErrorsToLogs: true,
      forwardReports: "all",
      sessionSampleRate: 100,
      storeContextsAcrossPages: true,
      trackSessionAcrossSubdomains: true,
      useSecureSessionCookie: true,
      // Prevent errors if accidentally initialized multiple times
      silentMultipleInit: true,
    });

    datadogRum.init({
      applicationId: "c9568ff5-acac-4874-83da-b7cd4248f23b",
      clientToken: "pub991a3cacb1eba9538678833aec0ac23b",
      site: "datadoghq.com",
      service: "web-next",
      env: getEnv(),
      allowedTracingUrls: [
        { match: "https://app-api.caltana.com", propagatorTypes: ["tracecontext", "datadog"] },
        { match: "https://dev-api.caltana.com", propagatorTypes: ["tracecontext", "datadog"] },
      ],
      version: process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 100,
      traceSampleRate: 100,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: "mask-user-input",
      trackSessionAcrossSubdomains: true,
      useSecureSessionCookie: true,
      // Prevent errors if accidentally initialized multiple times
      silentMultipleInit: true,
    });

    // Start session replay after successful initialization
    datadogRum.startSessionReplayRecording();
    return true;
  } catch (error) {
    // Log the error but don't break the app
    console.error("Failed to initialize Datadog:", error);
    return false;
  }
}

// Initialize immediately at module load time (browser only - "use client" guarantees this
// never runs on the server). This ensures RUM is active before React hydration so no
// early events (resources, interactions, long tasks) are missed.
const datadogInitialized = isDatadogEnabled() ? initializeDatadog() : false;

export default function DatadogInit() {
  const profileQuery = useOptionalAuthenticatedProfile();
  const user = profileQuery?.data;

  // Set user identity once Datadog is initialized and the profile has loaded
  useEffect(() => {
    if (!datadogInitialized || !user?.email) return;

    setDatadogUser(user);
  }, [user]);

  return null;
}
